import { useEffect, useRef } from 'react';
import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface FormActionButtonsProps {
	onSubmit?: () => void;
	isSubmitting?: boolean;
	/**
	 * Optional custom submit label. If not provided, uses translation based on mode.
	 * @deprecated Prefer using mode prop instead
	 */
	submitLabel?: string;
	/**
	 * Form mode to determine the submit button label
	 * 'create' shows "Create", 'edit' shows "Save changes", 'clone' shows "Create" (same as create)
	 */
	mode?: 'create' | 'edit' | 'clone';
	submitType?: 'submit' | 'button';
	submitVariant?:
		| 'default'
		| 'destructive'
		| 'outline'
		| 'secondary'
		| 'ghost'
		| 'link';
	disabled?: boolean;
	className?: string;
	/**
	 * Validation errors from react-hook-form formState.errors
	 * Used to show inline validation error message
	 */
	validationErrors?: Record<string, any>;
	/**
	 * Callback to scroll to first error field
	 * If not provided, default behavior will attempt to focus first invalid field
	 */
	onScrollToError?: () => void;
	/**
	 * Form ID to associate the submit button with a form element.
	 * Required when the button is rendered outside the form (e.g., in header).
	 * The form element should have a matching id attribute.
	 */
	formId?: string;
}

export const FormActionButtons = ({
	onSubmit,
	isSubmitting = false,
	submitLabel,
	mode = 'edit',
	submitType = 'submit',
	submitVariant = 'default',
	disabled = false,
	className = '',
	validationErrors = {},
	onScrollToError,
	formId
}: FormActionButtonsProps) => {
	const { t } = useTranslation();
	const isDisabled = disabled || isSubmitting;
	const submitButtonRef = useRef<HTMLButtonElement>(null);

	// Handle Ctrl+Enter keyboard shortcut for form submission
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			// Only handle Ctrl+Enter
			if (e.key !== 'Enter' || !e.ctrlKey) return;

			// Skip if already handled by another component (e.g., autocomplete)
			if (e.defaultPrevented) return;

			// Skip if disabled or submitting
			if (isDisabled) return;

			// Trigger form submission
			e.preventDefault();
			if (submitType === 'submit' && formId) {
				// Submit the associated form
				const form = document.getElementById(formId) as HTMLFormElement;
				if (form) {
					form.requestSubmit();
				}
			} else if (onSubmit) {
				onSubmit();
			} else if (submitButtonRef.current) {
				submitButtonRef.current.click();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isDisabled, submitType, formId, onSubmit]);

	// Use translations with fallback to custom labels
	const getDefaultLabel = () => {
		switch (mode) {
			case 'create':
				return t('buttons.create');
			case 'clone':
				return t('buttons.clone');
			default:
				return t('form.saveChanges');
		}
	};
	const finalSubmitLabel = submitLabel ?? getDefaultLabel();

	// Count total validation errors (including nested fields)
	const countErrors = (errors: Record<string, any>): number => {
		let count = 0;
		for (const key in errors) {
			const error = errors[key];
			if (error) {
				if (Array.isArray(error)) {
					// Handle array fields (e.g., vehicles in contracts)
					error.forEach(item => {
						if (item && typeof item === 'object') {
							count += countErrors(item);
						}
					});
				} else if (typeof error === 'object' && error.message) {
					// Direct error with message
					count++;
				} else if (typeof error === 'object') {
					// Nested object errors
					count += countErrors(error);
				}
			}
		}
		return count;
	};

	const errorCount = countErrors(validationErrors);
	const hasErrors = errorCount > 0;

	// console.log(validationErrors);


	// Delay for smooth scroll animation before attempting focus
	const FOCUS_DELAY_MS = 300;

	const handleScrollToError = () => {
		if (onScrollToError) {
			onScrollToError();
		} else {
			// Default behavior: find and focus first invalid field
			const firstError = document.querySelector('[aria-invalid="true"]');
			if (firstError) {
				firstError.scrollIntoView({
					behavior: 'smooth',
					block: 'center'
				});
				// Try to focus the element if it's focusable
				if (firstError instanceof HTMLElement) {
					setTimeout(() => firstError.focus(), FOCUS_DELAY_MS);
				}
			}
		}
	};

	return (
		<div className={`flex items-center gap-4 ${className}`}>
			{/* Submit button with keyboard shortcut hint */}
			<div className="flex items-center">
				<Button
					ref={submitButtonRef}
					type={submitType}
					variant={submitVariant}
					onClick={onSubmit}
					disabled={isDisabled}
					form={formId}
					className="gap-2"
				>
					{isSubmitting ? t('form.saving') : finalSubmitLabel}
					{!isSubmitting && (
						<span className={`hidden sm:inline text-[10px] font-normal opacity-60 ${isDisabled ? 'opacity-30' : ''}`}>
							Ctrl+Enter
						</span>
					)}
				</Button>
			</div>
			{/* Validation errors */}
			{hasErrors && !isSubmitting && (
				<Alert
					variant="destructive"
					className="transition-colors cursor-pointer hover:bg-destructive/5 py-2"
					onClick={handleScrollToError}
				>
					<AlertCircle className="w-4 h-4" />
					<AlertDescription>
						{errorCount === 1
							? t('form.validationErrorSingular')
							: t('form.validationErrorPlural', { count: errorCount })}
					</AlertDescription>
				</Alert>
			)}
		</div>
	);
};
